<template>
  <Modal class="session-setup" v-if="modals.sessionSetup" @close="closeModal">
    <h3>{{ $t("session.createRoom") }}</h3>

    <div class="input-group">
      <label>{{ $t("session.enterRoomNumber") }}</label>
      <input
        type="text"
        v-model="roomNumber"
        :placeholder="$t('session.roomNumberPlaceholder')"
        @keyup.enter="confirmSetup"
        ref="roomInput"
      />
    </div>

    <div class="input-group">
      <label>{{ $t("session.enterPlayerCount") }}</label>
      <input
        type="number"
        v-model.number="playerCount"
        min="5"
        max="20"
        :placeholder="$t('session.playerCountPlaceholder')"
        @keyup.enter="confirmSetup"
      />
    </div>

    <div class="error-message" v-if="errorMessage">
      {{ errorMessage }}
    </div>

    <div class="button-group">
      <div class="button confirm" @click="confirmSetup">
        {{ $t("common.confirm") }}
      </div>
      <div class="button cancel" @click="closeModal">
        {{ $t("common.cancel") }}
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "./Modal";
import { mapState } from "vuex";

export default {
  components: {
    Modal,
  },
  data() {
    return {
      roomNumber: Math.round(Math.random() * 10000).toString(),
      playerCount: 7,
      errorMessage: "",
    };
  },
  computed: {
    ...mapState(["modals"]),
  },
  methods: {
    confirmSetup() {
      // Validate room number
      if (!this.roomNumber || this.roomNumber.trim() === "") {
        this.errorMessage = this.$t("session.errorEmptyRoomNumber");
        return;
      }

      // Validate player count
      if (
        isNaN(this.playerCount) ||
        this.playerCount < 5 ||
        this.playerCount > 20
      ) {
        this.errorMessage = this.$t("session.invalidPlayerCount");
        return;
      }

      // Emit event with the data
      this.$emit("confirm", {
        sessionId: this.roomNumber.trim(),
        playerCount: this.playerCount,
      });

      this.closeModal();
    },
    closeModal() {
      this.errorMessage = "";
      this.$store.commit("toggleModal", "sessionSetup");
    },
  },
  mounted() {
    // Auto-focus on room number input
    this.$nextTick(() => {
      if (this.$refs.roomInput) {
        this.$refs.roomInput.focus();
        this.$refs.roomInput.select();
      }
    });
  },
  watch: {
    "modals.sessionSetup"(isOpen) {
      if (isOpen) {
        // Reset values when modal opens
        this.roomNumber = Math.round(Math.random() * 10000).toString();
        this.playerCount = 7;
        this.errorMessage = "";
        // Focus input
        this.$nextTick(() => {
          if (this.$refs.roomInput) {
            this.$refs.roomInput.focus();
            this.$refs.roomInput.select();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../vars.scss";

.session-setup {
  h3 {
    margin: 0 0 20px 0;
    text-align: center;
  }

  .input-group {
    margin-bottom: 20px;

    label {
      display: block;
      margin-bottom: 10px;
      font-size: 18px;
      font-weight: bold;
      color: white;
      text-shadow: 0 0 4px black;
    }

    input {
      width: 100%;
      padding: 12px 16px;
      font-size: 16px;
      border: none;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.95);
      color: #333;
      box-sizing: border-box;

      &:focus {
        outline: none;
        background: white;
        box-shadow: 0 0 10px rgba(0, 123, 255, 0.5);
      }

      &[type="number"] {
        -moz-appearance: textfield;
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }
    }
  }

  .error-message {
    margin: 10px 0;
    padding: 10px;
    background: rgba(255, 0, 0, 0.2);
    border: 1px solid rgba(255, 0, 0, 0.5);
    border-radius: 5px;
    color: #ff6b6b;
    font-size: 14px;
    text-align: center;
  }

  .button-group {
    display: flex;
    gap: 15px;
    margin-top: 25px;

    .button {
      flex: 1;
      padding: 12px 20px;
      border-radius: 10px;
      font-size: 16px;
      font-weight: bold;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

      &.confirm {
        background: linear-gradient(180deg, #4a90e2 0%, #357abd 100%);
        color: white;

        &:hover {
          background: linear-gradient(180deg, #5ba3ff 0%, #4a90e2 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4);
        }

        &:active {
          transform: translateY(0);
        }
      }

      &.cancel {
        background: linear-gradient(180deg, #e74c3c 0%, #c0392b 100%);
        color: white;

        &:hover {
          background: linear-gradient(180deg, #ff6b5a 0%, #e74c3c 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
        }

        &:active {
          transform: translateY(0);
        }
      }
    }
  }
}
</style>
